import { FC } from 'react';

import { Image, Button, Icon } from '@shared';
import classnames from 'classnames';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';

import { useToggle, useInputError } from '../../../_hooks';
import InputWrapper, { InputWrapperProps } from '../InputWrapper';
import './fileInput.scss';

const MB_MULTIPLIER = 1000000;

export enum FileInputType {
  All = 'ALL',
  Image = 'IMAGE',
}

const requirements: Record<FileInputType, { accept?: string[]; extensions: string[]; maxSize: number }> = {
  [FileInputType.All]: {
    accept: null,
    extensions: ['.*'],
    maxSize: 3 * MB_MULTIPLIER,
  },
  [FileInputType.Image]: {
    accept: ['image/*'],
    extensions: ['.png', '.jpg', '.jpeg'],
    maxSize: 3 * MB_MULTIPLIER,
  },
  // All mime types here: https://www.iana.org/assignments/media-types/media-types.xhtml
};

type TProps = InputWrapperProps & {
  maxAmountOfFiles?: number;
  onChange: (files: File[], name: string) => void;
  type: FileInputType;
  value?: File[];
};

function formatSize(size: number): string {
  return `${(size / MB_MULTIPLIER).toFixed(1)} MB`;
}

const FileInput: FC<TProps> = ({ maxAmountOfFiles = 6, onChange, type, value = [], ...wrapperProps }) => {
  const { disabled, validation, name } = wrapperProps;
  const { t } = useTranslation();
  const { setDirty, showError } = useInputError(validation);
  const [isDropping, setIsDropping] = useToggle(false);
  const [isError, setIsError] = useToggle(false);
  const isDisabled = disabled || value.length === maxAmountOfFiles;
  const { accept, maxSize, extensions } = requirements[type];

  const { getRootProps, getInputProps } = useDropzone({
    accept,
    disabled: isDisabled,
    maxSize,
    multiple: maxAmountOfFiles > 1,
    onDragLeave: () => setIsDropping(false),
    onDragOver: () => {
      setIsDropping(true);
      setIsError(false);
    },
    onDrop: (acceptedFiles: File[], fileRejections: unknown[]) => {
      onChange([...value, ...acceptedFiles.filter(file => !value.some(f => f.name === file.name))], name);
      setDirty();
      setIsDropping(false);
      setIsError(fileRejections.length > 0);
    },
  });

  function renderTrigger() {
    return (
      <div className="trigger">
        <Icon name="Upload" size={5.5} />
        <span className="title">{t('SHARED.FILE_UPLOAD.TITLE')}</span>
        <span className="requirements">
          {t('SHARED.FILE_UPLOAD.REQUIREMENTS', {
            extensions: extensions.join(', '),
            size: formatSize(maxSize),
          })}
        </span>
      </div>
    );
  }

  function renderPreview(file: File) {
    return (
      <div className="preview" key={file.name}>
        <Image alt={file.name} src={URL.createObjectURL(file)} />
        <div className="details">
          <span className="name">{file.name}</span>
          <span className="size">{formatSize(file.size)}</span>
        </div>
        <Button
          disabled={disabled}
          onClick={() => {
            onChange(
              value.filter(f => f !== file),
              name,
            );
            setDirty();
          }}
        >
          <Icon name="Trash" />
          <span>{t('SHARED.BUTTONS.DELETE')}</span>
        </Button>
      </div>
    );
  }

  return (
    <InputWrapper {...wrapperProps} showError={showError}>
      <div
        {...getRootProps({
          className: classnames('dropzone', {
            active: isDropping,
            'animated-error': isError,
            disabled,
            error: showError,
            'no-hover': isDisabled,
          }),
        })}
      >
        {(!isDisabled || value.length === 0) && (
          <>
            <input name={name} {...getInputProps()} />
            {renderTrigger()}
          </>
        )}
        {value.map(renderPreview)}
      </div>
    </InputWrapper>
  );
};

export default FileInput;
